import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import styles from './Style.module.css'
import CustomModal from '../../../Components/Modal/CustomModal'
import Attendance from '../../../Components/Attendance/Attendance'
import { useGlobalContext } from '../../../Context/GlobalContext'
import DropDownIcon from '../../../Components/Icons/DropDownIcon'
import NotWorkingWarningPopup from '../../../Components/Popups/NotWorkingWarningPopup/NotWorkingWarningPopup'
import WorkHoursPopup from '../../../Pages/Therapists/Componets/WorkHours/WorkHoursPopup'
import { getTherapistTimings } from '../../../APICalling/APICalling'
import { checkActiveBookings } from '../../../Utils/checkActiveBookings'
import Echo from '../../../Components/Sockect/Echo'

const TherapistCard = ({ header, onAttendanceChange, staffTiming, activeDropdownId, setActiveDropdownId }) => {
  const navigate = useNavigate();
  const [showAttendance, setShowAttendance] = useState(false);
  const [isOnShift, setIsOnShift] = useState(false);
  const [isCheckedOut, setIsCheckedOut] = useState(false);
  const [showNotWorkingWarning, setShowNotWorkingWarning] = useState(false);
  const [showWorkHoursPopup, setShowWorkHoursPopup] = useState(false);
  const [shiftInfo, setShiftInfo] = useState(null);
  const [weekDates, setWeekDates] = useState([]);
  const attendanceChannelRef = useRef(null);
  const { selectedDate, selectedOutlet, loggedOutletID, isViewOnlyOutlet } = useGlobalContext();
  
  const showDropdown = activeDropdownId === header?.id;
  const isBookingExtended = Number(header?.is_booking_extended) === 1;


  const formatDateToDDMMYYYY = (date) => {
    try {
      return date?.toLocaleDateString('en-GB', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      }).replace(/\//g, '-');
    } catch { return null; }
  };

  const refreshStatus = () => {
    try {
      const day = formatDateToDDMMYYYY(selectedDate);
      if (!header?.id || !day) return;

      const staffId = String(header.id);
      const entry = staffTiming?.[staffId]?.[day]?.[0];

      const green = !!entry?.checkin_daily && !entry?.checkout_daily;
      const red = !!entry?.checkout_daily;

      setIsOnShift(green);
      setIsCheckedOut(red);
    } catch {
      setIsOnShift(false);
      setIsCheckedOut(false);
    }
  };

  useEffect(() => {
    refreshStatus();
    const handler = () => refreshStatus();
    window.addEventListener('staffTimingUpdate', handler);
    return () => window.removeEventListener('staffTimingUpdate', handler);
  }, [header?.id, selectedDate, staffTiming]);

  useEffect(() => {
    if (!Echo || !header?.id) return;

    const channelName = "attendance";
    const eventName = "attendance.updated";

    try {
      const channel = Echo.channel(channelName);
      attendanceChannelRef.current = channel;

      const handleEvent = (event) => {
        try {
          // The event payload structure is nested: { type, data: { ...fields }, message }
          const payload = event.data || event; 
          if (!payload) return;
          const therapistId = payload.therapist_id || payload.user_id;
          // Check if this event belongs to the current therapist card
          if (therapistId && (String(therapistId) === String(header.id))) {
            // Direct state update based on payload
            if (payload.checkin_daily || payload.checkout_daily) {
                 const isCheckedIn = !!payload.checkin_daily;
                 const isCheckedOut = !!payload.checkout_daily;
                 // Green dot: Checked in AND NOT Checked out
                 // Red dot: Checked out
                 const showGreen = isCheckedIn && !isCheckedOut;
                 const showRed = isCheckedOut;
                 setIsOnShift(showGreen);
                 setIsCheckedOut(showRed);
            }else if(payload.checkin_daily===null && payload.checkout_daily===null) {
                setIsOnShift(false);
                setIsCheckedOut(false);
            }
          }
        } catch (err) {
            console.error("Error in socket event handler:", err);
        }
      };

      channel.listen(eventName, handleEvent);
      channel.listen(`.${eventName}`, handleEvent);

      return () => {
        if (attendanceChannelRef.current) {
             channel.stopListening(eventName);
             channel.stopListening(`.${eventName}`);
        }
      };
    } catch (error) {
      console.error("Error setting up therapist attendance socket listener:", error);
    }
  }, [header?.id, selectedDate, selectedOutlet]);

  // Listen for break card click to open attendance popup
  useEffect(() => {
    const handleOpenAttendance = (event) => {
      const { therapist_id } = event.detail || {};
      if (therapist_id && Number(therapist_id) === Number(header?.id)) {
        setShowAttendance(true);
      }
    };
    
    window.addEventListener('openAttendancePopup', handleOpenAttendance);
    return () => window.removeEventListener('openAttendancePopup', handleOpenAttendance);
  }, [header?.id]);

  const handleToggleDropdown = (e) => {
    e.stopPropagation();
    if (isViewOnlyOutlet) {
      toast.warning("You have view-only access for this outlet.");
      return;
    }
    setActiveDropdownId(showDropdown ? null : header?.id);
  };
  
  const handleCloseAttendance = () => {
    setShowAttendance(false);
    setActiveDropdownId(null);
  };

  const handleDropdownOptionClick = async (option) => {
    setActiveDropdownId(null);
    if (option === 'attendance') {
      setShowAttendance(true);
    } else if (option === 'editAttendance') {
      navigate(`/therapists?therapist=${header?.id}&tab=attendance`);
    } else if (option === 'schedule') {
      // Redirect to therapists page with therapist ID and workHours tab
      navigate(`/therapists?therapist=${header?.id}&tab=workHours`);
    } else if (option === 'notWorking') {
      await handleSetNotWorking();
    }
  };

  const formatDateForAPI = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getWeekDates = (date) => {
    const dates = [];
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    startOfWeek.setDate(diff);
    
    for (let i = 0; i < 7; i++) {
      const weekDate = new Date(startOfWeek);
      weekDate.setDate(startOfWeek.getDate() + i);
      dates.push(weekDate);
    }
    return dates;
  };

  const [forceOffAnyway, setForceOffAnyway] = useState(false);


  const handleSetNotWorking = async () => {
    setForceOffAnyway(false); // Reset the flag
    if (!header?.id || !selectedDate) return;

    try {
      // Get week dates for the WorkHoursPopup
      const dates = getWeekDates(selectedDate);
      setWeekDates(dates);

      // Fetch shift info to check for bookings
      const endDate = new Date(selectedDate);
      endDate.setDate(endDate.getDate() + 6);

      const params = {
        user: header.id,
        outlet: selectedOutlet || loggedOutletID,
        start_date: formatDateForAPI(selectedDate),
        end_date: formatDateForAPI(endDate),
        leave: 0,
      };

      const response = await getTherapistTimings(params);

      let shift = null;
      if (response?.success && response?.data?.list) {
        const timingsData = response.data.list;
        const dateKey = formatDateForAPI(selectedDate);
        const therapistData = timingsData[String(header.id)];
        
        if (therapistData && therapistData[dateKey] && therapistData[dateKey].length > 0) {
          shift = therapistData[dateKey][0];
        }
      }

      // Check for active bookings in the whole day
      const hasBookings = await checkActiveBookings({
        therapistId: header.id,
        outletId: selectedOutlet || loggedOutletID,
        date: selectedDate,
      });
      
      // Update shift info with booking status
      if (shift) {
        shift.has_current_date_booking = hasBookings;
      } else {
        shift = { has_current_date_booking: hasBookings };
      }
      setShiftInfo(shift);

      // If there are bookings, set forceOffAnyway to true so leave_anyway=1 is sent
      if (hasBookings) {
        setForceOffAnyway(true);
      } else {
        setForceOffAnyway(false);
      }

      // Always open WorkHoursPopup directly
      setShowWorkHoursPopup(true);

    } catch (error) {
      console.error('Error checking bookings:', error);
      toast.error('Failed to check bookings');
    }
  };

  const handleForceNotWorking = () => {
    setForceOffAnyway(true);
    setShowNotWorkingWarning(false);
    setShowWorkHoursPopup(true);
  };

  const handleCloseNotWorkingWarning = () => {
    setShowNotWorkingWarning(false);
    setForceOffAnyway(false);
  };

  const handleCloseWorkHoursPopup = () => {
    setShowWorkHoursPopup(false);
    setShiftInfo(null);
    setForceOffAnyway(false);
  };

  const handleWorkHoursSuccess = async () => {
    // Fetch updated timing data
    try {
      const endDate = new Date(selectedDate);
      endDate.setDate(endDate.getDate() + 6);

      const params = {
        user: header.id,
        outlet: selectedOutlet || loggedOutletID,
        start_date: formatDateForAPI(selectedDate),
        end_date: formatDateForAPI(endDate),
        leave: 0,
      };

      const response = await getTherapistTimings(params);

      if (response?.success && response?.data?.list) {
        const timingsData = response.data.list;
        const staffId = String(header.id);
        
        // Update the staffTiming object with new data
        if (timingsData[staffId]) {
          Object.keys(timingsData[staffId]).forEach(dateKey => {
            if (!staffTiming[staffId]) staffTiming[staffId] = {};
            staffTiming[staffId][dateKey] = timingsData[staffId][dateKey];
          });
        }

        // Refresh status indicators
        refreshStatus();
        
        // Dispatch event to update other components
        window.dispatchEvent(new Event('staffTimingUpdate'));
      }
    } catch (error) {
      console.error('Error refreshing timing data:', error);
    }

    // Refresh the attendance/breaks data
    onAttendanceChange?.();
    setShowWorkHoursPopup(false);
    setShiftInfo(null);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown) {
        const isMenuContainer = event.target.closest(`.${styles.menuContainer}`);
        const isNameClick = event.target.closest(`.${styles.name} h4`);
        const isPagerClick = event.target.closest(`.${styles.left}`);
        
        if (!isMenuContainer && !isNameClick && !isPagerClick) {
          setActiveDropdownId(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown, setActiveDropdownId]);

  // Update parent th element z-index when dropdown opens
  useEffect(() => {
    const thElement = document.querySelector(`th[data-therapist-id="${header?.id}"]`);
    if (thElement) {
      if (showDropdown) {
        thElement.style.zIndex = '100';
      } else {
        thElement.style.zIndex = '';
      }
    }
  }, [showDropdown, header?.id]);


  const handleCheckIn = (updatedData) => {
  if (updatedData) {
    const day = formatDateToDDMMYYYY(selectedDate);
    const staffId = String(header.id);

    // Merge into staffTiming
    if (!staffTiming[staffId]) staffTiming[staffId] = {};
    staffTiming[staffId][day] = [updatedData];

    refreshStatus(); // update green dot
  }
  onAttendanceChange?.();
};

const handleCheckOut = (updatedData) => {
  if (updatedData) {
    const day = formatDateToDDMMYYYY(selectedDate);
    const staffId = String(header.id);

    if (!staffTiming[staffId]) staffTiming[staffId] = {};
    staffTiming[staffId][day] = [updatedData];

    refreshStatus(); // update red dot
  }
  onAttendanceChange?.();
};

const handleBreak = () => {
  // When a break is added/modified/deleted, refresh the breaks data
  onAttendanceChange?.();
};



  return (
    <>
      <div className={styles.therapistCard}>
        <div 
          className={`${styles.left} ${header?.gender?.toLowerCase() === "female" ? styles.female : styles.male}`}
          onClick={handleToggleDropdown}
          style={{ cursor: 'pointer' }}
          title={header?.pager_number ? header?.pager_number : <div style={{fontSize: '8px'}}>N/A</div>}
        >
          {header?.pager_number ? header?.pager_number : <div style={{fontSize: '8px'}}>N/A</div>}
        </div>
        <div className={styles.right}>
          <div className={styles.name}>
            <h4 
              title={header?.alias ? header?.alias : header?.name || <div style={{opacity: '0.5'}}>N/A</div> }
              onClick={handleToggleDropdown}
              style={{ cursor: 'pointer' }}
            >
              {header?.alias ? header?.alias : header?.name || <div style={{opacity: '0.5'}}>N/A</div> }
            </h4>
            {(isOnShift || isCheckedOut || isBookingExtended) && (
              <span className={styles.statusIcons}>
                {isOnShift && <span className={`${styles.greenDot} ${styles.dot}`} />}
                {isCheckedOut && <span className={`${styles.redDot} ${styles.dot}`} />}
                {/* {isBookingExtended && (
                  <span className={styles.extendedMoon} aria-hidden="true">🌙</span>
                )} */}
              </span>
            )}
            <div className={styles.menuContainer}>
              <button 
                className={styles.menuIcon} 
                onClick={handleToggleDropdown}
                type="button"
                title="Options"
              >
                <DropDownIcon 
                  size={10}
                  color="currentColor"
                  className={`${styles.dropdownIcon} ${showDropdown ? styles.iconOpen : ''}`}
                />
              </button>
              {showDropdown && (
                <div className={styles.dropdown}>
                  <div 
                    className={styles.dropdownItem}
                    onClick={() => handleDropdownOptionClick('attendance')}
                  >
                    Attendance and Break
                  </div>
                  <div 
                    className={styles.dropdownItem}
                    onClick={() => handleDropdownOptionClick('schedule')}
                  >
                    Edit Work Hours
                  </div>
                  <div 
                    className={styles.dropdownItem}
                    onClick={() => handleDropdownOptionClick('editAttendance')}
                  >
                    Edit Attendance
                  </div>
                  <div 
                    className={styles.dropdownItem}
                    onClick={() => handleDropdownOptionClick('notWorking')}
                  >
                    Set to Not Working
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* <p>{header?.gender || 'N/A' }</p> */}
        </div>
      </div>

      {showAttendance && (
        <CustomModal 
          show={showAttendance} 
          onHide={handleCloseAttendance}
          modalClass={styles.attendanceModal}
        >
          <Attendance 
            staff={header}
            onClose={handleCloseAttendance}
            onCheckIn={handleCheckIn}
            onBreak={handleBreak}
            onCheckOut={handleCheckOut}
            staffTiming={staffTiming}
          />
        </CustomModal>
      )}

      {showNotWorkingWarning && (
        <NotWorkingWarningPopup 
          onClose={handleCloseNotWorkingWarning} 
          onContinue={handleForceNotWorking}
        />
      )}

      {showWorkHoursPopup && (
        <WorkHoursPopup
          isOpen={showWorkHoursPopup}
          onClose={handleCloseWorkHoursPopup}
          selectedDate={selectedDate}
          weekDates={weekDates}
          shiftInfo={shiftInfo}
          therapistId={header?.id}
          onSuccess={handleWorkHoursSuccess}
          startWithNotWorking={true}
          leaveAnyway={forceOffAnyway}
        />
      )}
    </>
  )
}


export default TherapistCard
